import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { FirestoreError } from "firebase/firestore";
import { JobHeader } from "@/components/JobDetails/JobHeader";
import { useLayout } from "@/contexts/LayoutContext";
import { useJobDetailsData } from "@/hooks/useJobDetailsData";
import { useUpdateJobAccess } from "@/hooks/useUpdateJobAccess";
import { useMarkJobOpened } from "@/hooks/useMarkJobOpened";
import { JobDetailProvider } from "@/contexts/JobDetailContext";
import { JobStatus } from "@/hooks/useJobs";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/utils/toast";
import { LoadingSpinner } from "@/components/loading/LoadingSpinner";
import { useUpdateAcquisitionStatus } from "@/hooks/useUpdateAcquisitionStatus";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";

export const Route = createFileRoute("/jobs/$jobId")({
  component: JobDetailLayout,

  beforeLoad: async ({ params, location }) => {
    const basePath = `/jobs/${params.jobId}`;

    // Bare /jobs/$jobId → redirect to /overview, forwarding search params.
    // The overview route itself redirects to /summary when it would render no cards.
    if (location.pathname === basePath) {
      const searchParams = location.search as Record<string, unknown>;
      throw redirect({
        to: "/jobs/$jobId/overview",
        params: { jobId: params.jobId },
        search: {
          markedForDeletion:
            (searchParams.markedForDeletion as string) ?? undefined,
        },
      });
    }
  },

  validateSearch: (
    search: Record<string, unknown>,
  ): { markedForDeletion?: string | undefined } => ({
    markedForDeletion:
      typeof search?.markedForDeletion === "string"
        ? search.markedForDeletion
        : search?.markedForDeletion != null
          ? String(search.markedForDeletion)
          : undefined,
  }),
});

export function JobDetailLayout() {
  const { navbarHeightRem } = useLayout();
  const { jobId } = Route.useParams();
  const { t } = useTranslation(["jobDetails"]);
  const { logEvent } = useAnalytics();
  const navigate = useNavigate({ from: Route.fullPath });
  const { isLoggedIn, authUser } = useAuth();
  const { markOpened } = useMarkJobOpened();
  const hasLoggedEvent = useRef(false);
  const hasRedirected = useRef(false);
  const markedOpenedJobId = useRef<string | null>(null);

  const { job, summaryData, isLoading, error } = useJobDetailsData(jobId);
  const {
    collaborators,
    ownerEmail,
    accessRevoked,
    addCollaborators,
    removeCollaborator,
  } = useUpdateJobAccess({
    jobId,
    job,
    manuscriptTitle: summaryData?.title ?? "",
    manuscriptAuthor: summaryData?.author ?? "",
    fileName: job?.fileName ?? "",
  });

  useEffect(() => {
    if (hasRedirected.current || !isLoggedIn) return;
    const dataErrorCode = (error as FirestoreError | null)?.code;
    if (!accessRevoked && dataErrorCode !== "permission-denied") return;

    hasRedirected.current = true;
    toast.error(t("redirectToasts.accessRevoked"));
    navigate({
      to: "/home",
      search: { redirect: undefined, downloadJobId: undefined },
    });
  }, [accessRevoked, error, isLoggedIn, navigate, t]);

  useEffect(() => {
    if (isLoading || hasRedirected.current || !isLoggedIn) return;

    if (!job) {
      hasRedirected.current = true;
      toast.error(t("redirectToasts.loadFailed"));
      navigate({
        to: "/home",
        search: { redirect: undefined, downloadJobId: undefined },
      });
      return;
    }

    if (job.status === JobStatus.DELETED) {
      hasRedirected.current = true;
      toast.info(t("redirectToasts.deleted"));
      navigate({
        to: "/home",
        search: { redirect: undefined, downloadJobId: undefined },
      });
      return;
    }

    if (job.status !== JobStatus.COMPLETED) {
      hasRedirected.current = true;
      if (job.status === JobStatus.ERROR) {
        toast.error(t("redirectToasts.error"));
      } else {
        toast.loading(t("redirectToasts.notReady"));
      }
      navigate({
        to: "/home",
        search: { redirect: undefined, downloadJobId: undefined },
      });
    }
  }, [job, isLoading, isLoggedIn, navigate, t]);

  useEffect(() => {
    if (
      isLoading ||
      !job ||
      job.status !== JobStatus.COMPLETED ||
      hasLoggedEvent.current
    )
      return;

    hasLoggedEvent.current = true;
    logEvent("detail_view_opened", {
      job_id: jobId,
      status: job.status,
    });
  }, [job, isLoading, jobId, logEvent]);

  useEffect(() => {
    if (isLoading || !job || job.status !== JobStatus.COMPLETED) {
      return;
    }

    const isSharedView = !!authUser && job.userId !== authUser.uid;
    if (!isSharedView) return;

    // Write once per job: `job` is rebuilt every render and markOpened's own
    // write re-renders via the shared_jobs snapshot, so an unguarded effect
    // loops forever.
    if (markedOpenedJobId.current === jobId) return;
    markedOpenedJobId.current = jobId;

    markOpened(jobId);
  }, [job, isLoading, authUser, jobId, markOpened]);

  const { updateAcquisitionStatus } = useUpdateAcquisitionStatus(job?.jobId);
  const hasProcessedDeepLink = useRef(false);
  const { features } = useFeatureFlags();
  const { markedForDeletion } = Route.useSearch();

  useEffect(() => {
    if (!job || job.status !== JobStatus.COMPLETED) return;
    if (markedForDeletion?.toLowerCase() !== "false") return;
    if (hasProcessedDeepLink.current) return;
    hasProcessedDeepLink.current = true;

    const clearParam = () => {
      navigate({
        search: (prev) => ({
          ...prev,
          markedForDeletion: undefined,
        }),
        replace: true,
      });
    };

    if (!features.manuscriptDeletion) {
      clearParam();
      return;
    }

    clearParam();

    (async () => {
      const success = await updateAcquisitionStatus(false);
      if (success) {
        toast.success(t("jobHeader.deepLink.acquired"));
      } else {
        toast.error(t("jobHeader.acquisitionStatus.toast.updateFailed"));
      }
    })();
  }, [
    markedForDeletion,
    job,
    features.manuscriptDeletion,
    navigate,
    updateAcquisitionStatus,
    t,
  ]);

  if (isLoading || !job || job.status !== JobStatus.COMPLETED) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="64px" />
      </div>
    );
  }

  return (
    <JobDetailProvider
      summaryData={summaryData}
      isLoading={isLoading}
      job={job}
      collaborators={collaborators}
      ownerEmail={ownerEmail}
      addCollaborators={addCollaborators}
      removeCollaborator={removeCollaborator}
    >
      <div className="relative flex min-h-screen w-full flex-col">
        <div style={{ paddingTop: `${navbarHeightRem}rem` }}>
          <JobHeader
            job={job}
            summaryData={summaryData}
            isLoading={isLoading}
          />
          <Outlet />
        </div>
      </div>
    </JobDetailProvider>
  );
}
